import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, ArrowLeft, Lock, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AFFILIATE_LINKS } from '../constants/affiliates';

const questions = [
    { id: "anx1",  text: "I experience a persistent hyper-vigilance regarding the potential withdrawal of my partner's affection.", axis: "anxiety", reverse: false },
    { id: "anx2",  text: "I frequently calculate the risk of my partner’s eventual departure from the relationship.", axis: "anxiety", reverse: false },
    { id: "anx3",  text: "I am acutely sensitive to the possibility that my partner’s interest in me is diminishing.", axis: "anxiety", reverse: false },
    { id: "anx4",  text: "I often sense a deficit in emotional reciprocity that my professional success cannot bridge.", axis: "anxiety", reverse: false },
    { id: "anx5",  text: "I find myself over-functioning to ensure I remain indispensable to my partner.", axis: "anxiety", reverse: false },
    { id: "anx6",  text: "My internal stability is significantly disrupted when my partner is emotionally inaccessible.", axis: "anxiety", reverse: false },
    { id: "anx7",  text: "I worry that if I am not constantly providing value, my partner will lose interest.", axis: "anxiety", reverse: false },
    { id: "anx8",  text: "I experience a sense of \"quiet terror\" when my partner does not respond to my outreach immediately.", axis: "anxiety", reverse: false },
    { id: "anx9",  text: "I am preoccupied with the fear that I will eventually be replaced.", axis: "anxiety", reverse: false },
    { id: "anx10", text: "I often feel that I am more invested in the \"Relational ROI\" than my partner is.", axis: "anxiety", reverse: false },
    { id: "anx11", text: "I have a high need for explicit, frequent reassurance of my partner’s commitment.", axis: "anxiety", reverse: false },
    { id: "anx12", text: "When I sense a distance, I feel a physical compulsion to close the gap immediately.", axis: "anxiety", reverse: false },
    { id: "anx13", text: "I am often frustrated that my partner does not recognize my emotional needs without me stating them.", axis: "anxiety", reverse: false },
    { id: "anx14", text: "I find it difficult to maintain my \"Executive Presence\" when I feel insecure in my relationship.", axis: "anxiety", reverse: false },
    { id: "anx15", text: "I worry that my \"Too-Muchness\" will eventually drive my partner away.", axis: "anxiety", reverse: false },
    { id: "anx16", text: "I am prone to over-analyzing small shifts in my partner’s tone or body language.", axis: "anxiety", reverse: false },
    { id: "anx17", text: "I find myself \"auditioning\" for my partner's love even after the relationship is established.", axis: "anxiety", reverse: false },
    { id: "anx18", text: "I often feel that my emotional safety is entirely dependent on my partner’s current mood.", axis: "anxiety", reverse: false },
    { id: "avo1",  text: "I view extreme emotional transparency as a strategic liability.", axis: "avoidance", reverse: false },
    { id: "avo2",  text: "I find it difficult to depend on others for emotional support; I prefer to remain a \"Single Point of Failure.\"", axis: "avoidance", reverse: false },
    { id: "avo3",  text: "I view interdependence as a functional asset rather than a threat to my autonomy.", axis: "avoidance", reverse: true },
    { id: "avo4",  text: "I experience a \"Withdrawal Reflex\" when a partner attempts to move toward high-intensity intimacy.", axis: "avoidance", reverse: false },
    { id: "avo5",  text: "I prefer to keep my private internal world compartmentalized from my romantic partner.", axis: "avoidance", reverse: false },
    { id: "avo6",  text: "I find comfort in the vulnerability required for deep relational bonding.", axis: "avoidance", reverse: true },
    { id: "avo7",  text: "I often use work or professional obligations as a \"Prestige Shield\" to create emotional distance.", axis: "avoidance", reverse: false },
    { id: "avo8",  text: "I am comfortable with a partner seeing me in a state of non-performance or \"softness.\"", axis: "avoidance", reverse: true },
    { id: "avo9",  text: "I feel \"suffocated\" when a partner requests more of my time or emotional bandwidth.", axis: "avoidance", reverse: false },
    { id: "avo10", text: "I find it more efficient to solve my emotional problems alone than to involve a partner.", axis: "avoidance", reverse: false },
    { id: "avo11", text: "I believe that relying on a partner is a sign of psychological health, not weakness.", axis: "avoidance", reverse: true },
    { id: "avo12", text: "I often feel that my partners want to get \"too close\" too quickly.", axis: "avoidance", reverse: false },
    { id: "avo13", text: "I am cautious about making long-term commitments that might limit my personal sovereignty.", axis: "avoidance", reverse: false },
    { id: "avo14", text: "I find it easy to be emotionally open with my partner.", axis: "avoidance", reverse: true },
    { id: "avo15", text: "I prefer relationships that remain \"low-maintenance\" and do not require heavy cognitive labor.", axis: "avoidance", reverse: false },
    { id: "avo16", text: "I do not feel the need to \"hide\" parts of myself to maintain my partner's respect.", axis: "avoidance", reverse: true },
    { id: "avo17", text: "I am prone to \"deactivating\" my feelings when a relationship becomes too demanding.", axis: "avoidance", reverse: false },
    { id: "avo18", text: "I view \"needing\" someone as a direct threat to my professional and personal identity.", axis: "avoidance", reverse: false }
];

const profiles = {
    secure: {
        title: "The Sovereign Leader",
        subtitle: "Your relational system is optimized for high-intensity intimacy and high-performance autonomy.",
        diagnostic: "Secure Infrastructure",
        failure: "N/A (System is highly regulated)",
        summary: "Your attachment infrastructure is robust. You do not view intimacy as a threat to your autonomy, nor do you view autonomy as a threat to your intimacy. This allows you to lead with clarity and engage in relationships without the cognitive load of constant threat-detection. You can depend on others and allow them to depend on you, creating a compounding return on relational investment.",
        impacts: [
            { aspect: "Partner Attraction", desc: "You attract and are attracted to other secure, high-functioning individuals who value mutual growth." },
            { aspect: "Partner Needed", desc: "A peer. Someone who can match your capacity for both independence and deep connection." },
            { aspect: "Conflict Style", desc: "Direct, regulated, and solution-oriented. You repair ruptures quickly without attacking character." },
            { aspect: "Stress Response", desc: "You lean on your support system appropriately while maintaining internal locus of control." },
            { aspect: "Parental Style & Legacy", desc: "Fosters a secure base for the next generation, modeling healthy emotional regulation and consistent availability. Leaves a legacy of relational resilience." },
            { aspect: "Intimacy & Sexual Function", desc: "Experiences high levels of trust and mutual vulnerability. Integrates emotional connection seamlessly with physical intimacy." }
        ]
    },
    anxious: {
        title: "The Over-Functioner",
        subtitle: "You use your competence to \"bribe\" for safety. Your system is currently in a state of chronic hyper-vigilance.",
        diagnostic: "Anxious-Preoccupied",
        failure: "Self-abandonment for connection.",
        summary: "Your executive success is partly fueled by a deep-seated fear of not being 'enough.' In relationships, this manifests as chronic hyper-vigilance. You are constantly reading the room, anticipating needs, and over-delivering to ensure you remain indispensable. This 'Relational ROI' tracking is exhausting and often leads to burnout, as you secretly resent the lack of reciprocal effort while simultaneously fearing that demanding it will cause your partner to leave.",
        impacts: [
            { aspect: "Partner Attraction", desc: "Often drawn to avoidant partners whose distance triggers your need to 'earn' their love." },
            { aspect: "Partner Needed", desc: "Someone highly consistent, communicative, and willing to provide explicit reassurance without viewing it as a burden." },
            { aspect: "Conflict Style", desc: "Pursuing. You feel an urgent need to resolve issues immediately to alleviate internal anxiety." },
            { aspect: "Stress Response", desc: "Hyper-activation. You seek immediate external regulation and may become overwhelmed if it's unavailable." },
            { aspect: "Parental Style & Legacy", desc: "May exhibit inconsistent boundaries or over-involvement, driven by a fear of disconnection. The legacy often includes heightened emotional reactivity and a deep craving for reassurance." },
            { aspect: "Intimacy & Sexual Function", desc: "Often uses physical intimacy as a barometer for relationship security. May experience anxiety around performance or fear that a partner's waning interest signals abandonment." }
        ]
    },
    dismissive: {
        title: "The Hyper-Independent",
        subtitle: "You have mistaken isolation for strength. Your \"Prestige Mask\" is preventing the intimacy you actually require.",
        diagnostic: "Dismissive-Avoidant",
        failure: "Preemptive disconnection.",
        summary: "You have built a formidable 'Prestige Shield.' You view extreme emotional transparency as a liability and prefer to remain a single point of failure. While this hyper-independence serves you well in high-stakes professional environments, it creates a profound sense of isolation in your personal life. You often feel suffocated by the emotional demands of others and preemptively distance yourself before anyone can get close enough to hurt you or demand too much bandwidth.",
        impacts: [
            { aspect: "Partner Attraction", desc: "Often attract anxious partners, which reinforces your belief that relationships are demanding and suffocating." },
            { aspect: "Partner Needed", desc: "Someone highly autonomous who does not pressure you for intimacy and respects your need for extensive compartmentalization." },
            { aspect: "Conflict Style", desc: "Withdrawing. You tend to shut down, intellectualize, or physically leave to avoid emotional flooding." },
            { aspect: "Stress Response", desc: "Deactivation. You suppress emotional needs and hyper-focus on actionable, solitary problem-solving." },
            { aspect: "Parental Style & Legacy", desc: "Tends to emphasize independence and self-reliance, sometimes at the expense of emotional attunement. The legacy can be one of stoicism and emotional distance." },
            { aspect: "Intimacy & Sexual Function", desc: "May compartmentalize physical intimacy from emotional vulnerability. Often prefers physical connection that doesn't demand deep emotional entanglement or loss of autonomy." }
        ]
    },
    fearful: {
        title: "The Volatile Visionary",
        subtitle: "You are trapped in a \"Push-Pull\" loop, oscillating between a craving for closeness and a terror of betrayal.",
        diagnostic: "Fearful-Avoidant",
        failure: "Systemic relational chaos.",
        summary: "Your relational infrastructure is highly volatile. You possess a deep, unmet craving for intense intimacy, yet you are simultaneously terrified of the vulnerability it requires. This creates a painful 'push-pull' dynamic. When a partner is distant, your anxiety spikes and you pursue them; when they get too close, your avoidance triggers and you push them away. This oscillation consumes massive amounts of cognitive and emotional energy, often destabilizing your executive focus.",
        impacts: [
            { aspect: "Partner Attraction", desc: "Drawn to chaotic or unpredictable dynamics that mirror your internal state of relational danger." },
            { aspect: "Partner Needed", desc: "An incredibly grounded, patient, and secure partner who can weather the storms without taking the push-pull personally." },
            { aspect: "Conflict Style", desc: "Reactive and unpredictable. Can swing rapidly from desperate pursuit to cold withdrawal." },
            { aspect: "Stress Response", desc: "Disorganized. You may seek support but then reject it when offered, feeling fundamentally unsafe." },
            { aspect: "Parental Style & Legacy", desc: "Can display unpredictable parenting patterns, oscillating between intrusive and withdrawn behaviors. The legacy may involve passing down unresolved trauma or relational confusion." },
            { aspect: "Intimacy & Sexual Function", desc: "Experiences a push-pull dynamic in intimate settings, desiring closeness but fearing vulnerability. Physical intimacy can be fraught with conflicting needs for connection and self-protection." }
        ]
    }
};

import { dbService } from '../services/dbService';
import { auth } from '../lib/firebase';

export default function ExecutiveAttachmentAudit() {
    const [step, setStep] = useState<'INTRO' | 'ASSESSMENT' | 'RESULTS'>('INTRO');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, number>>({});
    const [isCompiling, setIsCompiling] = useState(false);

    const handleStart = () => setStep('ASSESSMENT');

    const handleAnswer = (value: number) => {
        const question = questions[currentQuestionIndex];
        setAnswers(prev => ({ ...prev, [question.id]: value }));
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const calculateResults = () => {
        let anxSum = 0;
        let avoSum = 0;

        questions.forEach(q => {
            let val = answers[q.id] || 4; // default neutral
            if (q.reverse) val = 8 - val;
            if (q.axis === 'anxiety') anxSum += val;
            else avoSum += val;
        });

        const anxAvg = anxSum / 18;
        const avoAvg = avoSum / 18;

        let resultProfile;
        if (anxAvg <= 4 && avoAvg <= 4) resultProfile = profiles.secure;
        else if (anxAvg > 4 && avoAvg <= 4) resultProfile = profiles.anxious;
        else if (anxAvg <= 4 && avoAvg > 4) resultProfile = profiles.dismissive;
        else resultProfile = profiles.fearful;

        return { profile: resultProfile, anxAvg, avoAvg };
    };

    const handleNext = async () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            setStep('RESULTS');
            setIsCompiling(true);
            
            // Save to DB
            try {
                const user = auth.currentUser;
                if (!user) throw new Error("User not found");

                const { profile, anxAvg, avoAvg } = calculateResults();
                await dbService.saveAuditResult(user.uid, {
                    auditType: 'executive_attachment',
                    title: 'Executive Attachment Audit',
                    diagnostic: profile.diagnostic,
                    summary: profile.summary,
                    scoreAnxiety: anxAvg,
                    scoreAvoidance: avoAvg
                });
            } catch (err) {
                console.error("Failed to persist audit results:", err);
            }

            setTimeout(() => setIsCompiling(false), 3000);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen flex flex-col font-sans text-theme-text bg-theme-bg selection:bg-gold/30">
            <main className="flex-grow flex flex-col items-center justify-start w-full pt-20 pb-24">
                <AnimatePresence mode="wait">
                    {step === 'INTRO' && (
                        <motion.div
                            key="intro"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="flex flex-col items-center text-center max-w-2xl mx-auto px-6 py-12"
                        >
                            <h1 className="font-serif text-4xl md:text-5xl uppercase tracking-[0.15em] text-theme-text mb-8 leading-tight">
                                The Executive Attachment Infrastructure Audit
                            </h1>
                            <div className="w-16 h-[1px] bg-gold mb-8"></div>
                            <p className="text-theme-text/80 text-lg leading-relaxed mb-12 font-light">
                                A high-end diagnostic tool designed to assess your relational architecture.
                                Discover how your attachment style impacts your leadership, autonomy, and capacity for high-intensity intimacy.
                            </p>
                            <button
                                onClick={handleStart}
                                className="group relative px-8 py-4 bg-transparent border border-gold text-theme-text uppercase tracking-widest text-sm font-medium overflow-hidden transition-all hover:text-white cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out -z-10"></div>
                                <span className="flex items-center gap-3 relative z-10 group-hover:text-theme-bg transition-colors">
                                    Commence Audit <ArrowRight className="w-4 h-4" />
                                </span>
                            </button>
                        </motion.div>
                    )}

                    {step === 'ASSESSMENT' && (
                        <motion.div
                            key="assessment"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="w-full max-w-3xl mx-auto px-6 py-12 flex flex-col items-center pt-24"
                        >
                            <div className="w-full max-w-xl h-[1px] bg-theme-border flex-grow-0 mb-12 relative">
                                <motion.div
                                    className="absolute top-0 left-0 h-full bg-gold shadow-[0_0_8px_rgba(212,175,55,0.5)]"
                                    initial={{ width: 0 }}
                                    animate={{ width: ((currentQuestionIndex) / questions.length) * 100 + '%' }}
                                    transition={{ duration: 0.5 }}
                                />
                            </div>

                            <div className="text-center mb-16">
                                <span className="font-serif text-gold tracking-widest uppercase text-base md:text-lg font-semibold">
                                    Diagnostic {currentQuestionIndex + 1} / {questions.length}
                                </span>
                            </div>

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentQuestionIndex}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="w-full flex flex-col items-center"
                                >
                                    <h2 className="text-2xl md:text-3xl font-light text-theme-text text-center leading-relaxed mb-16 min-h-[120px] flex items-center italic">
                                        {"\"" + questions[currentQuestionIndex].text + "\""}
                                    </h2>

                                    <div className="w-full max-w-3xl mx-auto">
                                        <div className="flex flex-wrap justify-center sm:flex-nowrap sm:justify-between items-start gap-3 sm:gap-2 w-full">
                                            {[
                                                { val: 1, label: "Strongly Disagree" },
                                                { val: 2, label: "Disagree" },
                                                { val: 3, label: "Slightly Disagree" },
                                                { val: 4, label: "Neutral" },
                                                { val: 5, label: "Slightly Agree" },
                                                { val: 6, label: "Agree" },
                                                { val: 7, label: "Strongly Agree" }
                                            ].map(({ val, label }) => {
                                                const isSelected = answers[questions[currentQuestionIndex].id] === val;
                                                return (
                                                    <div key={val} className="flex flex-col items-center gap-3 w-1/4 sm:w-auto">
                                                        <button
                                                            onClick={() => handleAnswer(val)}
                                                            className={"relative w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center transition-all duration-300 cursor-pointer shrink-0 " + (isSelected ? "border-gold bg-gold text-theme-bg scale-110 shadow-[0_0_15px_rgba(212,175,55,0.4)]" : "border-theme-border text-theme-text hover:border-gold hover:text-gold hover:scale-110 hover:shadow-[0_0_15px_rgba(212,175,55,0.2)]")}
                                                        >
                                                            <span className="text-sm sm:text-base font-medium">{val}</span>
                                                        </button>
                                                        <span className={"text-[8px] sm:text-[10px] uppercase tracking-widest text-center leading-tight transition-colors mt-2 max-w-[80px] " + (isSelected ? "text-gold font-bold" : "text-taupe")}>
                                                            {label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="flex justify-between w-full mt-24 px-4 border-t border-theme-border pt-8">
                                            <button
                                                onClick={handlePrev}
                                                disabled={currentQuestionIndex === 0}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold " + (currentQuestionIndex === 0 ? "text-taupe/30 cursor-not-allowed" : "text-taupe hover:text-gold cursor-pointer")}
                                            >
                                                <ArrowLeft className="w-4 h-4" /> Previous
                                            </button>
                                            <button
                                                onClick={handleNext}
                                                disabled={!answers[questions[currentQuestionIndex].id]}
                                                className={"flex items-center gap-2 text-xs uppercase tracking-widest transition-colors font-bold px-6 py-3 border inline-flex " + (!answers[questions[currentQuestionIndex].id] ? "border-transparent text-taupe/30 cursor-not-allowed" : "border-gold text-gold hover:bg-gold hover:text-theme-bg cursor-pointer")}
                                            >
                                                {currentQuestionIndex === questions.length - 1 ? 'Complete Audit' : 'Next Step'} <ArrowRight className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </motion.div>
                    )}

                    {step === 'RESULTS' && (() => {
                        if (isCompiling) {
                            return (
                                <motion.div
                                    key="compiling"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="w-full max-w-4xl mx-auto px-6 py-24 flex flex-col items-center justify-center min-h-[50vh]"
                                >
                                    <div className="relative w-24 h-24 mb-12">
                                        <div className="absolute inset-0 border-t-2 border-l-2 border-gold rounded-full animate-[spin_2s_linear_infinite]"></div>
                                        <div className="absolute inset-2 border-r-2 border-b-2 border-taupe rounded-full animate-[spin_3s_linear_infinite_reverse]"></div>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Lock className="w-6 h-6 text-gold animate-pulse" />
                                        </div>
                                    </div>
                                    <h2 className="font-serif text-xl md:text-2xl uppercase tracking-[0.2em] text-gold mb-4 text-center">
                                        Compiling Diagnostic Profile
                                    </h2>
                                    <p className="text-theme-text/60 font-light text-sm tracking-widest animate-pulse text-center">
                                        Analyzing relational architecture...
                                    </p>
                                </motion.div>
                            );
                        }

                        const { profile } = calculateResults();
                        
                        const containerVariants = {
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: { staggerChildren: 0.2, delayChildren: 0.1 }
                            }
                        };

                        const itemVariants = {
                            hidden: { opacity: 0, y: 20 },
                            visible: { 
                                opacity: 1, y: 0, 
                                transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } as any
                            }
                        };

                        return (
                            <motion.div
                                key="results"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="w-full max-w-4xl mx-auto px-6 py-12 pb-24"
                            >
                                <motion.div variants={itemVariants} className="text-center mb-16">
                                    <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Confidential Briefing</span>
                                    <h1 className="font-serif text-4xl md:text-5xl uppercase tracking-widest text-theme-text mt-6 mb-6">
                                        {profile.title}
                                    </h1>
                                    <p className="text-lg text-theme-text/70 font-light max-w-2xl mx-auto italic">
                                        {"\"" + profile.subtitle + "\""}
                                    </p>
                                </motion.div>

                                <motion.div variants={itemVariants} className="w-full h-[1px] bg-gold/30 mb-16" />

                                <motion.div variants={itemVariants} className="grid md:grid-cols-2 gap-12 md:gap-16 mb-16">
                                    <div>
                                        <h3 className="font-serif text-xl uppercase tracking-widest text-theme-text mb-6 flex items-center gap-3">
                                            <div className="w-2 h-2 bg-gold rotate-45"></div>
                                            Core Diagnostic
                                        </h3>
                                        <div className="bg-black/20 p-8 border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 shadow-md backdrop-blur-sm group">
                                            <p className="mb-6">
                                                <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2">Attachment Style:</strong>
                                                <span className="text-xl font-serif text-gold italic">{profile.diagnostic}</span>
                                            </p>
                                            <p>
                                                <strong className="font-medium text-theme-text uppercase text-xs tracking-[0.2em] block mb-2">Primary Relational Failure:</strong>
                                                <span className="text-theme-text/80 font-light">{profile.failure}</span>
                                            </p>
                                        </div>
                                    </div>

                                    <div>
                                        <h3 className="font-serif text-xl uppercase tracking-widest text-theme-text mb-6 flex items-center gap-3">
                                            <div className="w-2 h-2 bg-gold rotate-45"></div>
                                            Executive Summary
                                        </h3>
                                        <p className="text-theme-text/80 font-light leading-relaxed text-justify">
                                            {profile.summary}
                                        </p>
                                    </div>
                                </motion.div>

                                <motion.div variants={itemVariants} className="w-full h-[1px] bg-gold/30 mb-16" />

                                <motion.div variants={itemVariants} className="mb-24">
                                    <h3 className="font-serif text-2xl uppercase tracking-widest text-theme-text mb-10 text-center">
                                        Secondary Systemic Impacts
                                    </h3>
                                    <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                                        {profile.impacts.map((impact, idx) => (
                                            <div key={idx} className="bg-black/20 p-6 md:p-8 border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 shadow-md backdrop-blur-sm group">
                                                <h4 className="font-serif text-lg text-gold mb-3 group-hover:text-gold/90 transition-colors">{impact.aspect}</h4>
                                                <p className="text-theme-text/80 font-light text-sm leading-relaxed">{impact.desc}</p>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>

                                <motion.div variants={itemVariants} className="bg-black/20 p-8 md:p-12 text-center relative overflow-hidden shadow-2xl border border-solid border-gold/30 hover:border-gold/100 transition-colors duration-300 backdrop-blur-sm group">
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gold"></div>
                                    <h3 className="font-serif text-xl md:text-2xl uppercase tracking-widest mb-4 text-theme-text">Recommended Infrastructure Upgrades</h3>
                                    <p className="text-theme-text/60 font-light text-sm mb-10 max-w-xl mx-auto leading-relaxed">
                                        To further optimize your relational architecture, we recommend the following curated resources.
                                    </p>
                                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                                        <a href={AFFILIATE_LINKS.ONLINE_THERAPY} target="_blank" rel="noopener noreferrer" className="px-6 py-4 border border-theme-border hover:border-gold hover:bg-gold/10 hover:text-gold font-bold transition-all text-xs uppercase tracking-widest text-theme-text">Online-Therapy.com</a>
                                        <a href={AFFILIATE_LINKS.BOOKS.ATTACHED} target="_blank" rel="noopener noreferrer" className="px-6 py-4 border border-theme-border hover:border-gold hover:bg-gold/10 hover:text-gold font-bold transition-all text-xs uppercase tracking-widest text-theme-text">AUDIBLE: ATTACHED</a>
                                        <Link to="/vault" className="px-6 py-4 border border-theme-border hover:border-gold hover:bg-gold/10 hover:text-gold font-bold transition-all text-xs uppercase tracking-widest text-theme-text">VIP Vault Resources</Link>
                                    </div>
                                </motion.div>
                            </motion.div>
                        );
                    })()}
                </AnimatePresence>
            </main>
        </div>
    );
}
